// FormBuilderAdvancedOptions — the properties panel's "Advanced options"
// accordion: one collapsible row per property group (Tickets · Data validation
// · Layout & styling · Media & Notes · Others). All sections are built except
// Tickets (coming-soon shell, own chunk). Rows render only when the selected
// element's type actually carries that property group (e.g. image has no
// tickets/validation/media/others; a page has validation/layout/others).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type MentionOption } from '@/components/common';
import type { ElementProperties } from './utils/formElementDefaults';
import FormBuilderValidationSection from './FormBuilderValidationSection';
import FormBuilderLayoutSection from './FormBuilderLayoutSection';
import FormBuilderMediaSection from './FormBuilderMediaSection';
import FormBuilderOthersSection from './FormBuilderOthersSection';
import type { BuilderField, BuilderPage, GeofenceGate } from './utils/builderModel';

const SECTIONS = [
  { key: 'tickets', group: 'ticketProperties', labelKey: 'forms_builder_props_adv_tickets' },
  {
    key: 'validation',
    group: 'validationProperties',
    labelKey: 'forms_builder_props_adv_validation',
  },
  { key: 'layout', group: 'layoutProperties', labelKey: 'forms_builder_props_adv_layout' },
  { key: 'media', group: 'mediaProperties', labelKey: 'forms_builder_props_adv_media' },
  { key: 'others', group: 'otherProperties', labelKey: 'forms_builder_props_adv_others' },
] as const;

interface FormBuilderAdvancedOptionsProps {
  field: BuilderField | BuilderPage;
  /** Replace the selected element's properties (immutable update). */
  onChange: (properties: ElementProperties) => void;
  /** Geofence gating for a selected location field — validation section only. */
  geofenceGate?: GeofenceGate;
  /** Watermark mention options — media section only. */
  watermarkMentions?: MentionOption[];
}

const FormBuilderAdvancedOptions = ({
  field,
  onChange,
  geofenceGate,
  watermarkMentions,
}: FormBuilderAdvancedOptionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [openSection, setOpenSection] = useState<string | null>(null);

  const sections = SECTIONS.filter((s) => s.group in field.properties);
  if (!sections.length) return null;

  return (
    <section className='form-builder-props__accordion'>
      <button
        type='button'
        className='form-builder-props__accordion-head'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('forms_builder_props_advanced')}
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={16} />
      </button>

      {open && (
        <div className='form-builder-props__accordion-body'>
          {sections.map((s) => (
            <div key={s.key} className='form-builder-props__adv-section'>
              <button
                type='button'
                className='form-builder-props__adv-head'
                aria-expanded={openSection === s.key}
                onClick={() => setOpenSection((prev) => (prev === s.key ? null : s.key))}
              >
                {t(s.labelKey)}
                <Icon name={openSection === s.key ? 'chevron-up' : 'chevron-down'} size={15} />
              </button>
              {openSection === s.key && (
                <div className='form-builder-props__adv-body'>
                  {s.key === 'validation' ? (
                    <FormBuilderValidationSection
                      field={field}
                      onChange={onChange}
                      geofenceGate={geofenceGate}
                    />
                  ) : s.key === 'layout' ? (
                    <FormBuilderLayoutSection field={field} onChange={onChange} />
                  ) : s.key === 'media' ? (
                    <FormBuilderMediaSection
                      field={field}
                      onChange={onChange}
                      watermarkMentions={watermarkMentions}
                    />
                  ) : s.key === 'others' ? (
                    <FormBuilderOthersSection field={field} onChange={onChange} />
                  ) : (
                    t('text_coming_soon')
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default FormBuilderAdvancedOptions;
